import React, { useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { InertIcon } from './InertIcon';
import { BaseSwitch } from './formElements/switches/BaseSwitch';
import { BaseSelectDropdown } from './formElements/selects/BaseSelectDropdown';
import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
import { IconSizes } from '@/constants/iconTypes';
import { AttributeKinds } from '@/constants/attributeTypes';
import { AvailableAttribute } from '@/schemas/quoteResponseSchema';
import { useCoverageSection, useCoverageValidation } from '@/hooks/use-quote';

interface CoverageSectionProps {
  symbol: string;
  apply_to: string;
  title: string;
  description: string;
  icon: string;
  available_attributes: AvailableAttribute[];
}

export const CoverageSection: React.FC<CoverageSectionProps> = ({
  symbol,
  apply_to,
  title,
  description,
  icon,
  available_attributes,
}) => {
  const { isRequired, isDeclined, handleAttributeChange, getCurrentValue } = useCoverageSection(
    symbol,
    apply_to,
    available_attributes
  );

  const editableAttributes = available_attributes.filter((attr) => attr.kind !== AttributeKinds.DECLINED);

  // Get quote attributes for validation errors
  const coverageValidation = useCoverageValidation(symbol, apply_to);
  const handleDeclinedToggle = useCallback(
    (enabled: boolean) => {
      handleAttributeChange(AttributeKinds.DECLINED, {
        declined: !enabled,
        description: '',
      });
    },
    [handleAttributeChange]
  );

  const handleAttributeSelect = useCallback(
    (attr: AvailableAttribute, selectedDescription: string) => {
      const fullSelection = attr.selections.find((sel) => sel.description === selectedDescription);
      if (fullSelection) {
        handleAttributeChange(attr.kind, fullSelection);
      }
    },
    [handleAttributeChange]
  );

  return (
    <Stack spacing={3}>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        gap={2}
      >
        <Box
          display='flex'
          alignItems='center'
          gap={2}
        >
          <InertIcon
            icon_name={icon}
            size={IconSizes.SMALL}
          />
          <Typography
            variant='h4'
            component='h3'
            fontWeight='medium'
            sx={{ lineHeight: 1.15 }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          display='flex'
          alignItems='center'
          gap={2}
        >
          {isRequired ? (
            <Typography
              variant='body2'
              color='text.secondary'
            >
              Required
            </Typography>
          ) : (
            <BaseSwitch
              checked={!isDeclined}
              onChange={handleDeclinedToggle}
              id={`${symbol}-${apply_to}-declined`}
              fireAnalytics={false}
            />
          )}
        </Box>
      </Box>

      <Typography
        variant='body1'
        color='text.secondary'
        sx={{ fontSize: '1rem' }}
      >
        {description}
      </Typography>

      {coverageValidation && !coverageValidation.valid && coverageValidation.message && (
        <Alert
          severity={AlertSeverity.ERROR}
          title={null}
          titleGutterBottom={null}
          text={coverageValidation.message}
        />
      )}

      {!isDeclined && editableAttributes.length > 0 && (
        <Stack spacing={3}>
          {editableAttributes.map((attr) => (
            <BaseSelectDropdown
              key={attr.kind}
              id={`${symbol}-${apply_to}-${attr.kind}`}
              label={attr.name}
              options={attr.selections.map((sel) => ({
                value: sel.description,
                label: sel.description,
              }))}
              selectProps={{
                value: getCurrentValue(attr.kind)?.description ?? '',
                onChange: (e) => handleAttributeSelect(attr, e.target.value),
              }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
